import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, BookMarked, Users, HeartHandshake } from "lucide-react";
import useReveal from "@/hooks/useReveal";
import { track } from "@/lib/analytics";

/**
 * MinisterioEObras, bloco DISCRETO no final da Home.
 * Três cards compactos lado a lado: Livros, Instituto, Autismo.
 * Estilo sóbrio para não competir com o devocional protagonista acima.
 */
const OBRAS = [
  {
    slug: "livros",
    href: "/livros",
    icon: BookMarked,
    eyebrow: "Obras publicadas",
    title: "Livros do pastor",
    text:
      "Estudos e devocionários que aprofundam cada tema, escritos com raízes bíblicas e experiência pastoral.",
    cta: "Conhecer a coleção",
  },
  {
    slug: "instituto",
    href: "/instituto",
    icon: Users,
    eyebrow: "Ação social",
    title: "Instituto Garoto da Arena",
    text:
      "Projeto que atende famílias em vulnerabilidade, transformando fé em cuidado concreto.",
    cta: "Conhecer o instituto",
  },
  {
    slug: "autismo",
    href: "/autismo",
    icon: HeartHandshake,
    eyebrow: "Acolhimento",
    title: "Autismo e fé",
    text:
      "Espaço de escuta, formação e acolhimento para famílias atípicas na jornada da fé.",
    cta: "Conhecer o projeto",
  },
];

export default function MinisterioEObras() {
  const r = useReveal();

  return (
    <section
      id="ministerio-e-obras"
      data-testid="ministerio-section"
      className="relative bg-[#F7F5F0] py-16 md:py-20 border-t border-[#0B1E36]/6"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-8">
        <div ref={r} className="reveal">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-8 bg-[#C5A059]/60" aria-hidden />
            <span className="text-[0.66rem] uppercase tracking-[0.32em] text-[#0B1E36]/60 font-medium">
              Ministério e obras
            </span>
          </div>
          <h2 className="font-serif-display text-[#0B1E36] text-[1.5rem] md:text-[1.85rem] lg:text-[2.15rem] leading-[1.15] tracking-[-0.015em] font-medium max-w-2xl">
            A palavra também caminha em obras concretas.
          </h2>

          <div
            data-testid="ministerio-cards"
            className="mt-10 grid md:grid-cols-3 gap-4 md:gap-5"
          >
            {OBRAS.map((o) => {
              const Icon = o.icon;
              return (
                <Link
                  key={o.slug}
                  to={o.href}
                  data-testid={`ministerio-card-${o.slug}`}
                  onClick={() =>
                    track("ministerio_click", {
                      slug: o.slug,
                      source: "home_ministerio_obras",
                    })
                  }
                  className="group bg-white border border-[#0B1E36]/8 p-6 md:p-7 hover:border-[#C5A059]/60 transition-colors flex flex-col"
                >
                  <span className="inline-flex items-center justify-center w-10 h-10 bg-[#0B1E36]/5 text-[#0B1E36] mb-4">
                    <Icon size={16} strokeWidth={1.6} />
                  </span>
                  <p className="text-[0.6rem] uppercase tracking-[0.3em] text-[#C5A059] font-medium">
                    {o.eyebrow}
                  </p>
                  <h3 className="mt-2 font-serif-display text-[1.25rem] md:text-[1.35rem] text-[#0B1E36] leading-[1.2] tracking-[-0.012em]">
                    {o.title}
                  </h3>
                  <p className="mt-3 text-[0.9rem] text-[#1F1A17]/65 leading-[1.65] font-light">
                    {o.text}
                  </p>
                  <div className="mt-5 pt-4 border-t border-[#0B1E36]/6 inline-flex items-center gap-2 text-[0.68rem] uppercase tracking-[0.26em] text-[#0B1E36] group-hover:text-[#C5A059] transition-colors">
                    <span>{o.cta}</span>
                    <ArrowUpRight
                      size={12}
                      strokeWidth={1.6}
                      className="ml-auto transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                    />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
